import React, { useEffect } from 'react';
import { Card, Form, Input, Button, Space, Typography, Row, Col, Divider, message } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Page from '../components/layout/Page';
import api from '../services/api';

const { Title } = Typography;

const Settings = () => {
  const { user, checkSession } = useAuth();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/');
        form.setFieldsValue({
          firstName: response.data.first_name,
          lastName: response.data.last_name,
          email: response.data.email,
          business_name: response.data.business_name,
          tax_number: response.data.tax_number,
          vat_id: response.data.vat_id,
          street: response.data.street,
          zip_code: response.data.zip_code,
          city: response.data.city,
          country: response.data.country,
          bank_name: response.data.bank_name,
          iban: response.data.iban,
          bic: response.data.bic,
          register_number: response.data.register_number,
          register_court: response.data.register_court,
          business_phone: response.data.business_phone,
          business_email: response.data.business_email,
          website: response.data.website,
        });
      } catch (error) {
        console.error('Error fetching settings:', error);
        message.error(t('settings.fetchError', 'Fehler beim Laden der Einstellungen'));
      }
    };

    fetchSettings();
  }, [form, t]);

  const onFinish = async (values) => {
    try {
      // Map form field names to API field names
      const data = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        business_name: values.business_name,
        tax_number: values.tax_number,
        vat_id: values.vat_id,
        street: values.street,
        zip_code: values.zip_code,
        city: values.city,
        country: values.country || 'Deutschland',
        bank_name: values.bank_name,
        iban: (values.iban || '').replace(/\s/g, '').toUpperCase(),
        bic: (values.bic || '').replace(/\s/g, '').toUpperCase(),
        register_number: values.register_number,
        register_court: values.register_court,
        business_phone: values.business_phone,
        business_email: values.business_email,
        website: values.website,
      };

      console.log('Sending data to API:', data);
      const response = await api.put('/settings/', data);
      console.log('API response:', response.data);
      
      await checkSession(); // Aktualisiere den User-Kontext
      message.success(t('settings.saveSuccess', 'Einstellungen erfolgreich gespeichert'));
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.response?.data) {
        console.error('API error details:', error.response.data);
      }
      message.error(t('settings.saveError', 'Fehler beim Speichern der Einstellungen'));
    }
  };

  const validateIBAN = (_, value) => {
    if (!value) {
      return Promise.resolve();
    }

    // Remove all spaces and convert to uppercase
    const cleanIBAN = value.replace(/\s/g, '').toUpperCase();

    // Check if it starts with DE
    if (!cleanIBAN.startsWith('DE')) {
      return Promise.reject(t('settings.ibanInvalidCountry'));
    }

    // Check length (German IBAN should be 22 characters)
    if (cleanIBAN.length !== 22) {
      return Promise.reject(t('settings.ibanInvalidLength'));
    }

    // Check if it only contains valid characters (letters and numbers)
    if (!/^[A-Z0-9]+$/.test(cleanIBAN)) {
      return Promise.reject(t('settings.ibanInvalidFormat'));
    }

    return Promise.resolve();
  };

  return (
    <Page
      title={t('settings.title', 'Einstellungen')}
      subtitle={t('settings.subtitle', 'Pflegen der Benutzer- und Rechnungsdaten')}
    >
      <Card style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            firstName: user?.first_name || '',
            lastName: user?.last_name || '',
            email: user?.email || '',
            // Business Information
            business_name: user?.business_name || '',
            tax_number: user?.tax_number || '',
            vat_id: user?.vat_id || '',
            // Address
            street: user?.street || '',
            zip_code: user?.zip_code || '',
            city: user?.city || '',
            country: user?.country || 'Deutschland',
            // Bank Information
            bank_name: user?.bank_name || '',
            iban: user?.iban || '',
            bic: user?.bic || '',
            // Registration
            register_number: user?.register_number || '',
            register_court: user?.register_court || '',
            // Contact
            business_phone: user?.business_phone || '',
            business_email: user?.business_email || '',
            website: user?.website || '',
          }}
          onFinish={onFinish}
        >
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Title level={4}>{t('settings.accountInfo', 'Benutzerdaten')}</Title>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="firstName"
                label={t('settings.firstName', 'Vorname')}
                rules={[{ required: true, message: t('settings.firstNameRequired', 'Bitte geben Sie Ihren Vornamen ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12}>
              <Form.Item
                name="lastName"
                label={t('settings.lastName', 'Nachname')}
                rules={[{ required: true, message: t('settings.lastNameRequired', 'Bitte geben Sie Ihren Nachnamen ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24}>
              <Form.Item
                name="email"
                label={t('settings.email', 'E-Mail')}
                rules={[
                  { required: true, message: t('settings.emailRequired', 'Bitte geben Sie Ihre E-Mail-Adresse ein!') },
                  { type: 'email', message: t('settings.emailInvalid', 'Bitte geben Sie eine gültige E-Mail-Adresse ein!') }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>       
            <Col span={24}>
              <Divider />
              <Title level={4}>{t('settings.addressandcontact', 'Adresse & Kontakt')}</Title>
            </Col>

            <Col span={24}>
              <Form.Item
                name="street"
                label={t('settings.street', 'Straße und Hausnummer')}
                rules={[{ required: true, message: t('settings.streetRequired', 'Bitte geben Sie Ihre Straße ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="zip_code"
                label={t('settings.zipCode', 'PLZ')}
                rules={[{ required: true, message: t('settings.zipCodeRequired', 'Bitte geben Sie Ihre PLZ ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="city"
                label={t('settings.city', 'Stadt')}
                rules={[{ required: true, message: t('settings.cityRequired', 'Bitte geben Sie Ihre Stadt ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="country"
                label={t('settings.country', 'Land')}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="business_phone"
                label={t('settings.businessPhone', 'Geschäftliche Telefonnummer')}
                rules={[{ required: true, message: t('settings.businessPhoneRequired', 'Bitte geben Sie Ihre geschäftliche Telefonnummer ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="business_email"
                label={t('settings.businessEmail', 'Geschäftliche E-Mail')}
                rules={[
                  { required: true, message: t('settings.businessEmailRequired', 'Bitte geben Sie Ihre geschäftliche E-Mail ein!') },
                  { type: 'email', message: t('settings.emailInvalid', 'Bitte geben Sie eine gültige E-Mail-Adresse ein!') }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="website"
                label={t('settings.website', 'Website')}
              >
                <Input />
              </Form.Item>
            </Col>



            <Col span={24}>
              <Divider />
              <Title level={4}>{t('settings.bankInfo', 'Bankverbindung')}</Title>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="bank_name"
                label={t('settings.bankName', 'Bank')}
                rules={[{ required: true, message: t('settings.bankNameRequired', 'Bitte geben Sie Ihre Bank ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="iban"
                label={t('settings.iban', 'IBAN')}
                rules={[
                  { required: true, message: t('settings.ibanRequired', 'Bitte geben Sie Ihre IBAN ein!') },
                  { validator: validateIBAN }
                ]}
                normalize={value => value ? value.replace(/\s/g, '').toUpperCase() : value}
              >
                <Input 
                  placeholder="DE00 0000 0000 0000 0000 00"
                  maxLength={27} // 22 chars + up to 5 spaces
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="bic"
                label={t('settings.bic', 'BIC/SWIFT')}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider />
              <Title level={4}>{t('settings.businessInfo', 'Geschäftliche Informationen')}</Title>
            </Col>

            <Col xs={24} sm={24}>
              <Form.Item
                name="business_name"
                label={t('settings.businessName', 'Geschäftsbezeichnung')}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="tax_number"
                label={t('settings.taxNumber', 'Steuernummer')}
                rules={[{ required: true, message: t('settings.taxNumberRequired', 'Bitte geben Sie Ihre Steuernummer ein!') }]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="vat_id"
                label={t('settings.vatId', 'USt-IdNr.')}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="register_number"
                label={t('settings.registerNumber', 'Handelsregisternummer')}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="register_court"
                label={t('settings.registerCourt', 'Registergericht')}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    {t('settings.save', 'Änderungen speichern')}
                  </Button>
                  <Button>
                    {t('settings.cancel', 'Abbrechen')}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </Page>
  );
};

export default Settings;
